import React from 'react'; // search form UI
import { Search, Loader2, ArrowLeft } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { LocationInput } from '@/components/common/LocationInput';
import { Input } from '@/components/ui/Input';

interface SearchFormProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSearch: () => void;
  handleClear: () => void;
  isSearching: boolean;
  onClose?: () => void;
}

export const SearchForm: React.FC<SearchFormProps> = ({
  formData,
  setFormData,
  handleInputChange,
  handleSearch,
  handleClear,
  isSearching,
  onClose
}) => {
  return (
    <aside className="w-full lg:w-[420px] shrink-0 border-b lg:border-b-0 lg:border-r border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 flex flex-col z-10 lg:h-full min-h-0 relative">
      <div className="flex-1 lg:overflow-y-auto p-4 lg:p-6 space-y-6 lg:space-y-8 scrollbar-thin scrollbar-thumb-white/10">
        <div>
          <button
              aria-label="Back to Options"
              onClick={onClose}
              className="mb-6 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer"
          >
              <ArrowLeft className="w-4 h-4" /> Back to Options
          </button>
          <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-3 px-1">Find Matching Jobs</h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm px-1">
            Describe your ideal role, skills, location, and requirements. AI will search the web for real, currently open jobs and match them against your profile.
          </p>
        </div>

        <div className="flex-1 flex flex-col gap-6 px-1">
          <Input
            label="Job Title *"
            type="text"
            name="jobTitle"
            id="jobTitle"
            aria-label="Job Title"
            placeholder="e.g. Frontend Developer"
            value={formData.jobTitle}
            onChange={handleInputChange}
          />
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-2">Seniority</label>
              <Select
                title="Seniority"
                value={formData.seniority}
                onChange={(val) => setFormData((prev: any) => ({ ...prev, seniority: val }))}
                options={[
                  { value: 'Any', label: 'Any' },
                  { value: 'Entry/Junior', label: 'Entry/Junior' },
                  { value: 'Mid-Level', label: 'Mid-Level' },
                  { value: 'Senior', label: 'Senior' },
                  { value: 'Lead', label: 'Lead' },
                  { value: 'Manager/Director', label: 'Manager/Director' }
                ]}
                placeholder="Select seniority..."
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-2">Company Size</label>
              <Select
                title="Company Size"
                value={formData.companySize}
                onChange={(val) => setFormData((prev: any) => ({ ...prev, companySize: val }))}
                options={[
                  { value: 'Any', label: 'Any' },
                  { value: '1-50 (Early Startup)', label: 'Early Startup (1-50)' },
                  { value: '51-200 (Scale-up)', label: 'Scale-up (51-200)' },
                  { value: '201-1000 (Mid-size)', label: 'Mid-size (201-1000)' },
                  { value: '1000+ (Enterprise)', label: 'Enterprise (1000+)' }
                ]}
                placeholder="Select size..."
              />
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-2">Location Type</label>
            <Select
              title="Location Type"
              value={formData.locationType}
              onChange={(val) => setFormData((prev: any) => ({ ...prev, locationType: val }))}
              options={[
                { value: 'Remote - In Country', label: 'Remote - In Country' },
                { value: 'Remote - Globally', label: 'Remote - Globally' },
                { value: 'Hybrid', label: 'Hybrid' },
                { value: 'On-site', label: 'On-site' },
                { value: 'Any', label: 'Any' }
              ]}
              placeholder="Select location type..."
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-2">Target Region/Country</label>
            <LocationInput
              value={formData.region}
              onChange={(val) => setFormData((prev: any) => ({ ...prev, region: val }))}
            />
          </div>

          <Input
            label="Key Skills"
            type="text"
            name="skills"
            id="skills"
            aria-label="Key Skills"
            placeholder="e.g. React, TypeScript, Node.js"
            value={formData.skills}
            onChange={handleInputChange}
          />
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Min. Salary"
              type="text"
              name="minSalary"
              id="minSalary"
              aria-label="Minimum Salary"
              placeholder="e.g. $90k, £60k"
              value={formData.minSalary}
              onChange={handleInputChange}
            />
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-2">Visa Option</label>
              <Select
                title="Visa Option"
                value={formData.visaSponsorship}
                onChange={(val) => setFormData((prev: any) => ({ ...prev, visaSponsorship: val }))}
                options={[
                  { value: 'Not Required', label: 'Not Required' },
                  { value: 'Required', label: 'Required' },
                  { value: 'Flexible', label: 'Flexible' }
                ]}
                placeholder="Select visa requirement..."
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-400 tracking-wider uppercase mb-2">Notes & Context</label>
            <textarea
              name="additionalInfo"
              id="additionalInfo"
              aria-label="Notes and Context"
              className="w-full text-sm border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 placeholder:text-slate-500 focus:border-slate-200 dark:focus:border-slate-700 focus:ring-1 focus:ring-emerald-500 outline-none resize-y min-h-[80px]"
              placeholder="e.g. Prefer Series B startups, no betting/gambling companies"
              value={formData.additionalInfo}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>
      
      <div className="p-3 lg:p-4 border-t border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0 relative">
         <div className="flex gap-3">
           <button
              type="button"
              aria-label="Clear Search Form"
              onClick={handleClear}
              className="px-4 py-2 flex items-center justify-center text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg cursor-pointer"
              title="Clear Search"
           >
              Clear
           </button>
           <button
              type="button"
              aria-label="Submit Search"
              onClick={handleSearch}
              disabled={!formData.jobTitle.trim() || isSearching}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500/50 text-sm font-medium rounded-lg px-3 py-2.5 lg:py-2 disabled:opacity-50 cursor-pointer shadow-sm disabled:cursor-not-allowed"
            >
              {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search Jobs
            </button>
         </div>
      </div>
    </aside>
  );
};
